import { IconButton, Menu, Toast, useToast } from '@zillow/constellation-10';
import { IconCopyFilled, IconMessagePlusFilled, IconMoreFilled } from '@zillow/constellation-icons';
import useModal from '../../hooks/useModal';
import { ModalType } from '../../types/ModalType';
import { createSavedReplyTextStore } from '../../lib/inboxStores';
import { logError } from '@zg-rentals/log-error';
import { getCurrentSessionURL } from '../../utils/fullStory';
import { useContext } from 'react';
import { InboxPageContext } from '../../contexts/InboxPageContextProvider';
import { css } from '@/styled-system/css';

const triggerVisibilityStyles = css.raw({
  opacity: 0,
  pointerEvents: 'none',
  minWidth: '36px',
  _after: {
    left: 0, // Overriding to remove horizontal scrollbar on mweb
  },
  transition: 'opacity 0.15s ease-in-out',
  '&.trigger-visible': {
    opacity: 1,
    pointerEvents: 'auto',
  },
});

type MessageActionMenuProps = {
  isTriggerVisible: boolean;
  messageContent: string;
  setIsMenuOpen: (isOpen: boolean) => void;
  setIsTriggerVisible: (shouldShow: boolean) => void;
};

export default function MessageActionMenu({
  isTriggerVisible,
  messageContent,
  setIsMenuOpen,
  setIsTriggerVisible,
}: MessageActionMenuProps) {
  const { enqueueToast } = useToast();
  const { setActiveModal } = useModal();
  const { listingAlias, conversationId } = useContext(InboxPageContext);

  const handleSave = () => {
    createSavedReplyTextStore.set(messageContent);
    setIsTriggerVisible(false);
    setActiveModal(ModalType.SavedReplyModal);
  };

  const handleCopyMessage = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      enqueueToast(<Toast tone="success">Message copied</Toast>);
    } catch (error) {
      logError({
        error,
        context: {
          details: 'MessageActionMenu - Error copying message to clipboard',
          text,
          conversationId,
          listingAlias,
          fullStorySessionUrl: getCurrentSessionURL(),
        },
      });
      enqueueToast(<Toast tone="critical">Message couldn’t be copied. Try again.</Toast>);
    } finally {
      setIsTriggerVisible(false);
    }
  };

  return (
    <Menu
      content={
        <>
          <Menu.Item onClick={() => handleCopyMessage(messageContent)}>
            <Menu.ItemIcon render={<IconCopyFilled />} />
            <Menu.ItemLabel>Copy</Menu.ItemLabel>
          </Menu.Item>
          <Menu.Item onClick={handleSave}>
            <Menu.ItemIcon render={<IconMessagePlusFilled />} />
            <Menu.ItemLabel>Save</Menu.ItemLabel>
          </Menu.Item>
        </>
      }
      onOpenChange={(isOpen) => {
        setIsMenuOpen(isOpen);

        if (!isOpen) {
          setIsTriggerVisible(false);
        }
      }}
      shouldAwaitInteractionResponse
      trigger={
        <IconButton
          className={isTriggerVisible ? 'trigger-visible' : ''}
          css={triggerVisibilityStyles}
          shape="circle"
          size="sm"
          title="Message Actions"
          icon={<IconMoreFilled />}
        />
      }
      placement="bottom-start"
    />
  );
}
